// Skizze der ganzen Wohnung je Etage (15.10.2026, Schritt 3 von 5): der Flur aus Abschnitten mit den Räumen an seinen
// Türen (flurskizze.ts), dazu Räume hinter einem Zimmer außen an ihrem Zimmer (Balkon hinter dem Wohnzimmer) und
// Verbindungstüren zwischen zwei Zimmern gestrichelt. Dieselbe Zeichnung in der Erfassung (Prüfseite) und im Bericht.
// Räume ohne Verbindung zum Flur (Keller, Dachboden, Garage) zeichnet sie nicht; die stehen im Text daneben.

import { hallSketchSvg, type SketchChild, type SketchDoor } from "./flurskizze.ts";
import type { HallSection, HallShape, HallSide, RoomWall } from "./konstanten.ts";

export type PlanRoomLike = {
  id: string;
  name: string;
  type: string;
  floor?: number | null;
  hallRoomId?: string | null;
  hallSide?: HallSide | null;
  hallOrder?: number | null;
  hallSection?: number | null;
  /** Lage der Tür an ihrer Wand 0..1 (30.10.2026). */
  hallPos?: number | null;
  hallShape?: HallShape | null;
  hallSections?: HallSection[] | null;
  doorWall?: RoomWall | null;
  doors?: { status: string; to: string | null }[] | null;
};

/** Flur einer Etage, an dem Türen hängen (Räume mit hallRoomId auf ihn). */
export function floorHall<R extends PlanRoomLike>(rooms: R[], floor: number): R | null {
  return rooms.find((h) => h.type === "flur" && (h.floor ?? 0) === floor && rooms.some((r) => r.hallRoomId === h.id)) ?? null;
}

/** Räume hinter einem Zimmer, verschachtelt. */
function childrenOf(rooms: PlanRoomLike[], id: string, seen: Set<string>): SketchChild[] {
  return rooms
    .filter((r) => r.hallRoomId === id && !seen.has(r.id))
    .map((r) => {
      seen.add(r.id);
      return { id: r.id, label: r.name, wall: r.doorWall ?? null, children: childrenOf(rooms, r.id, seen) };
    });
}

/** Verbindungstüren zwischen zwei Zimmern, ohne die Tür zum Elternraum (die zeichnet schon die Lage), jedes Paar einmal. */
export function roomLinks(rooms: PlanRoomLike[]): [string, string][] {
  const pairs = new Map<string, [string, string]>();
  for (const r of rooms)
    for (const d of r.doors ?? []) {
      if (d.status !== "raum" || !d.to || d.to === r.id) continue;
      const other = rooms.find((x) => x.id === d.to);
      if (!other || other.type === "flur" || r.type === "flur") continue;
      if (other.hallRoomId === r.id || r.hallRoomId === other.id) continue;
      const key = [r.id, other.id].sort().join("|");
      if (!pairs.has(key)) pairs.set(key, [r.id, other.id]);
    }
  return [...pairs.values()];
}

/** SVG der Etage oder null, wenn es dort keinen Flur mit Türen gibt. */
export function floorSketchSvg(rooms: PlanRoomLike[], floor: number, opts: { title?: string; numbered?: boolean } = {}): string | null {
  const hall = floorHall(rooms, floor);
  const onFloor = rooms.filter((r) => (r.floor ?? 0) === floor && r.type !== "flur");
  if (!hall && onFloor.length === 0) return null;
  if (!hall) return hallSketchSvg([], [], { title: opts.title ?? "Skizze der Etage", extras: onFloor.map((r) => ({ id: r.id, label: r.name, wall: null })), start: "Treppe", ohneGang: true });
  const seen = new Set<string>([hall.id]);
  const doorRooms = rooms.filter((r) => r.hallRoomId === hall.id).sort((a, b) => (a.hallOrder ?? 0) - (b.hallOrder ?? 0));
  for (const r of doorRooms) seen.add(r.id);
  const doors: SketchDoor[] = doorRooms.map((r, i) => ({
    id: r.id,
    label: r.name,
    section: r.hallSection ?? 1,
    side: r.hallSide ?? null,
    order: r.hallOrder ?? 0,
    pos: r.hallPos ?? null,
    ...(opts.numbered ? { nr: String(i + 1) } : {}),
    children: childrenOf(rooms, r.id, seen),
  }));
  // Räume dieser Etage ohne Flurtür und ohne Elternraum (Keller, Garage …) als Blasen dazu.
  const extras = onFloor.filter((r) => !seen.has(r.id) && !r.hallRoomId).map((r) => ({ id: r.id, label: r.name, wall: null, children: childrenOf(rooms, r.id, seen) }));
  return hallSketchSvg(hall.hallSections, doors, { title: opts.title ?? "Skizze der Wohnung", links: roomLinks(rooms), extras, start: floor === 0 ? "Eingang" : "Treppe" });
}
